import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Upload,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
  Download,
  Search
} from "lucide-react";

const CSVImport = ({ isOpen, onClose, onImportComplete }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.email === "admin@locofast.com";
  const isAdmin = ['admin','super_admin'].includes(user?.role);
  const [activeTab, setActiveTab] = useState(['admin','super_admin'].includes(user?.role) ? "import" : "update"); // "import" | "update" | "orders"
  const [step, setStep] = useState(1); // 1: upload, 2: preview/map, 3: result
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [bulkMatchBy, setBulkMatchBy] = useState("gst"); // "gst" or "phone" — key column for bulk update
  const fileInputRef = useRef(null);

  // Bulk Update state
  const [updateFile, setUpdateFile] = useState(null);
  const [updatePreview, setUpdatePreview] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);
  const [updateStep, setUpdateStep] = useState(1); // 1: upload, 2: preview, 3: result
  const updateFileRef = useRef(null);

  // Sample Upload state
  const [sampleFile, setSampleFile] = useState(null);
  const [samplePreview, setSamplePreview] = useState(null);
  const [sampleResult, setSampleResult] = useState(null);
  const [sampleStep, setSampleStep] = useState(1);
  const [isSampleProcessing, setIsSampleProcessing] = useState(false);
  const [sampleMatchBy, setSampleMatchBy] = useState("gst");
  const sampleFileRef = useRef(null);

  // Order Upload state — mirrors Sample Upload
  const [orderFile, setOrderFile] = useState(null);
  const [orderPreview, setOrderPreview] = useState(null);
  const [orderResult, setOrderResult] = useState(null);
  const [orderStep, setOrderStep] = useState(1);
  const [isOrderProcessing, setIsOrderProcessing] = useState(false);
  const [orderMatchBy, setOrderMatchBy] = useState("gst");
  const orderFileRef = useRef(null);

  // Weekly Combined Upload state (super admin only)
  // Single CSV: GST + optional Order Count → sets query flag for all rows + increments orders for count>0
  const [weeklyFile, setWeeklyFile] = useState(null);
  const [weeklyPreview, setWeeklyPreview] = useState(null);
  const [isUploadingWeekly, setIsUploadingWeekly] = useState(false);
  const [isExecutingWeekly, setIsExecutingWeekly] = useState(false);
  const [weeklyResult, setWeeklyResult] = useState(null);
  const [weeklyStep, setWeeklyStep] = useState(1);
  const [weeklyHistory, setWeeklyHistory] = useState([]);
  const weeklyFileRef = useRef(null);

  const availableFields = [
    { value: "company_name", label: "Company Name *" },
    { value: "data_source", label: "Data Source *" },
    { value: "name", label: "Contact Person Name *" },
    { value: "phone", label: "Phone *" },
    { value: "email", label: "Email *" },
    { value: "website", label: "Website" },
    { value: "city", label: "City *" },
    { value: "region", label: "Region *" },
    { value: "state", label: "State *" },
    { value: "gst_number", label: "GST Number *" },
    { value: "gst_registration_date", label: "GST Registration Date *" },
    { value: "gst_turnover", label: "GST Turnover *" },
    { value: "business_type", label: "Business Type *" },
    { value: "business_email", label: "Business Email" },
    { value: "fabric_category", label: "Fabric Category *" },
    { value: "fabric_type", label: "Fabric Type" },
    { value: "composition", label: "Composition" },
    { value: "address", label: "Address" },
    { value: "notes", label: "Notes" },
    { value: "customer_type", label: "Customer Type" },
    { value: "quantity", label: "Quantity" },
    { value: "previous_status", label: "Previous Status (Query/Order)" },
    { value: "majority_business_share", label: "Majority Business Share" },
    // Order History columns (Order 1-5)
    { value: "order_1_lead_id", label: "Order 1 - Lead ID" },
    { value: "order_1_fabric_type", label: "Order 1 - Fabric Type" },
    { value: "order_1_composition", label: "Order 1 - Composition" },
    { value: "order_1_date", label: "Order 1 - Date" },
    { value: "order_2_lead_id", label: "Order 2 - Lead ID" },
    { value: "order_2_fabric_type", label: "Order 2 - Fabric Type" },
    { value: "order_2_composition", label: "Order 2 - Composition" },
    { value: "order_2_date", label: "Order 2 - Date" },
    { value: "order_3_lead_id", label: "Order 3 - Lead ID" },
    { value: "order_3_fabric_type", label: "Order 3 - Fabric Type" },
    { value: "order_3_composition", label: "Order 3 - Composition" },
    { value: "order_3_date", label: "Order 3 - Date" },
    { value: "order_4_lead_id", label: "Order 4 - Lead ID" },
    { value: "order_4_fabric_type", label: "Order 4 - Fabric Type" },
    { value: "order_4_composition", label: "Order 4 - Composition" },
    { value: "order_4_date", label: "Order 4 - Date" },
    { value: "order_5_lead_id", label: "Order 5 - Lead ID" },
    { value: "order_5_fabric_type", label: "Order 5 - Fabric Type" },
    { value: "order_5_composition", label: "Order 5 - Composition" },
    { value: "order_5_date", label: "Order 5 - Date" },
    { value: "category_serviced_1", label: "Category Serviced 1" },
    { value: "category_serviced_2", label: "Category Serviced 2" },
    { value: "category_serviced_3", label: "Category Serviced 3" },
    { value: "_skip_", label: "-- Skip --" }
  ];

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const validExtensions = ['.csv', '.xls', '.xlsx'];
    const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(fileExt)) {
      toast.error("Please select a CSV, XLS, or XLSX file");
      return;
    }

    setFile(selectedFile);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      // First check if this is an update template (lightweight)
      const previewResp = await axios.post(`${API}/leads/import-csv/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const headers = (previewResp.data.headers || []).map(h => h.toLowerCase().replace('*','').trim());
      const isUpdate = headers.includes('phone') && 
        (headers.some(h => h.includes('business') && h.includes('type')) || headers.some(h => h.includes('gst') && h.includes('turnover'))) &&
        !headers.some(h => h.includes('company') || h.includes('data source') || h.includes('contact person'));

      if (isUpdate) {
        setIsUpdateMode(true);
        // Directly upload to update endpoint
        const updateFormData = new FormData();
        updateFormData.append('file', selectedFile);
        const response = await axios.post(`${API}/leads/update-fields`, updateFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setImportResult(response.data);
        setStep(3);
        if (response.data.updated > 0) {
          toast.success(`Updated ${response.data.updated} leads`);
          onImportComplete?.();
        }
      } else {
        setIsUpdateMode(false);
        setPreviewData(previewResp.data);
        setColumnMapping(previewResp.data.suggested_mapping || {});
        setStep(2);
      }
    } catch (error) {
      // Surface the actual backend reason. Detail can be a string, an array of
      // Pydantic validation errors, or an object — extract each case cleanly.
      let msg = "Failed to process file";
      const d = error?.response?.data?.detail;
      if (typeof d === 'string' && d.trim()) {
        msg = d;
      } else if (Array.isArray(d) && d.length) {
        msg = d.map(e => `${(e.loc || []).join('.')}: ${e.msg}`).filter(Boolean).join(' · ');
      } else if (d && typeof d === 'object') {
        msg = d.msg || d.message || JSON.stringify(d);
      } else if (error?.response?.status) {
        msg = `Upload failed (HTTP ${error.response.status}${error.response.statusText ? ' ' + error.response.statusText : ''})`;
      } else if (error?.message) {
        msg = `Upload failed: ${error.message}`;
      }
      console.error("Import preview failed", error?.response?.data || error);
      toast.error(msg, { duration: 8000 });
      setFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setIsImporting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('column_mapping', JSON.stringify(columnMapping));
      // Data source is now read from file rows, pass "Upload data" as default
      formData.append('data_source', 'Upload data');

      const response = await axios.post(`${API}/leads/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      setStep(3);
      
      if (response.data.imported > 0) {
        toast.success(response.data.message);
        onImportComplete?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import file");
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFile(null);
    setPreviewData(null);
    setColumnMapping({});
    setImportResult(null);
    setIsUpdateMode(false);
    setUpdateFile(null);
    setUpdatePreview(null);
    setUpdateResult(null);
    setUpdateStep(1);
    setWeeklyFile(null);
    setWeeklyPreview(null);
    setWeeklyResult(null);
    setWeeklyStep(1);
    onClose();
  };

  const handleWeeklyFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    setWeeklyFile(selectedFile);
    setIsUploadingWeekly(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const res = await axios.post(`${API}/leads/weekly-upload/preview`, formData);
      setWeeklyPreview(res.data);
      setWeeklyStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to preview weekly file");
      setWeeklyFile(null);
    } finally {
      setIsUploadingWeekly(false);
    }
  };

  const handleWeeklyExecute = async () => {
    if (!weeklyFile) return;
    setIsExecutingWeekly(true);
    try {
      const formData = new FormData();
      formData.append("file", weeklyFile);
      const res = await axios.post(`${API}/leads/weekly-upload/execute`, formData);
      setWeeklyResult(res.data);
      setWeeklyStep(3);
      if (onImportComplete) onImportComplete();
      toast.success(res.data.message || "Weekly upload applied");
      // Refresh history after successful execute
      try {
        const hist = await axios.get(`${API}/leads/weekly-upload/history`);
        setWeeklyHistory(hist.data || []);
      } catch (_) { /* non-critical */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply weekly upload");
    } finally {
      setIsExecutingWeekly(false);
    }
  };

  // Fetch weekly-upload history when the tab becomes active and the dialog is open (super admin only)
  useEffect(() => {
    if (!isOpen || !isSuperAdmin || activeTab !== "weekly") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/leads/weekly-upload/history`);
        if (!cancelled) setWeeklyHistory(res.data || []);
      } catch (_) {
        if (!cancelled) setWeeklyHistory([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isSuperAdmin, activeTab]);

  const formatRelative = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH} hr${diffH > 1 ? 's' : ''} ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 30) return `${diffD} day${diffD > 1 ? 's' : ''} ago`;
    return d.toLocaleDateString();
  };

  const handleUpdateFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    setUpdateFile(selectedFile);
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("mode", "update");
      formData.append("match_by", bulkMatchBy);
      const res = await axios.post(`${API}/leads/bulk-update/preview`, formData);
      setUpdatePreview(res.data);
      setUpdateStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to preview update");
    } finally {
      setIsUploading(false);
    }
  };

  const handleBulkUpdateExecute = async () => {
    if (!updateFile) return;
    setIsUpdating(true);
    try {
      const formData = new FormData();
      formData.append("file", updateFile);
      formData.append("mode", "update");
      formData.append("match_by", bulkMatchBy);
      const res = await axios.post(`${API}/leads/bulk-update/execute`, formData);
      setUpdateResult(res.data);
      setUpdateStep(3);
      if (onImportComplete) onImportComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to execute update");
    } finally {
      setIsUpdating(false);
    }
  };

  const updateMapping = (csvColumn, leadField) => {
    setColumnMapping(prev => ({
      ...prev,
      [csvColumn]: leadField
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Upload Data
          </DialogTitle>
          <DialogDescription>
            Import new leads or bulk update existing ones
          </DialogDescription>
        </DialogHeader>

        {/* Tab Toggle */}
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg overflow-x-auto">
          {isAdmin && (
            <button
              onClick={() => { setActiveTab("import"); setStep(1); }}
              className={`flex-1 min-w-[90px] py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${activeTab === "import" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="tab-import"
            >
              Import New
            </button>
          )}
          <button
            onClick={() => { setActiveTab("update"); setUpdateStep(1); }}
            className={`flex-1 min-w-[90px] py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${activeTab === "update" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-bulk-update"
          >
            Bulk Update
          </button>
          <button
            onClick={() => { setActiveTab("samples"); setSampleStep(1); }}
            className={`flex-1 min-w-[110px] py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${activeTab === "samples" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-sample-upload"
          >
            Sample Upload
          </button>
          <button
            onClick={() => { setActiveTab("orders"); setOrderStep(1); }}
            className={`flex-1 min-w-[110px] py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${activeTab === "orders" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-order-upload"
          >
            Order Upload
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => { setActiveTab("weekly"); setWeeklyStep(1); }}
              className={`flex-1 min-w-[110px] py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${activeTab === "weekly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="tab-weekly-upload"
            >
              Weekly Update
            </button>
          )}
        </div>

        {/* ===== BULK UPDATE TAB ===== */}
        {activeTab === "update" && (
          <>
            {updateStep === 1 && (
              <div className="py-4 space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1">
                  <p className="font-semibold">How it works:</p>
                  <p>- <strong>Match by</strong>: GST Number (default — India) or Phone Number (international / Bangladesh leads without GST)</p>
                  <p>- <strong>Has value</strong> in a cell → updates that field</p>
                  <p>- <strong>Empty cell</strong> → skipped (existing data preserved)</p>
                  <p>- Type <strong>BLANK</strong> to explicitly clear a field</p>
                  <p>- GST-auto fields (Company Name, Business Type, Reg Date, City, State, Address) are not updatable</p>
                  <p>- Updatable fields include: Website, Email, Phone, Contact Person, <strong>Designation, LinkedIn URL</strong>, <strong>Contact 2 (Name/Designation/Phone/Email/LinkedIn)</strong>, Payment Type, GST Turnover, Region, Data Source, Fabric Type/Category, Product Category, Primary Material, Material 1-4 + %, GSM, Width, Quantity, Pattern, Color, Finish, Notes, <strong>Assigned To</strong> (use agent email).</p>
                  <p>- For <strong>Assigned To</strong>: write the agent's login email or <strong>BLANK</strong> to unassign. Unmatched emails are skipped.</p>
                </div>

                {/* Match-by toggle (GST vs Phone) */}
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-medium text-slate-700">Match By:</span>
                  <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="bulk-match-by-toggle">
                    <button
                      type="button"
                      onClick={() => setBulkMatchBy("gst")}
                      className={`px-3 py-1 ${bulkMatchBy === "gst" ? "bg-blue-500 text-white" : "bg-white text-slate-600 hover:bg-blue-50"}`}
                      data-testid="bulk-match-gst"
                    >GST Number</button>
                    <button
                      type="button"
                      onClick={() => setBulkMatchBy("phone")}
                      className={`px-3 py-1 border-l border-slate-200 ${bulkMatchBy === "phone" ? "bg-blue-500 text-white" : "bg-white text-slate-600 hover:bg-blue-50"}`}
                      data-testid="bulk-match-phone"
                    >Phone Number</button>
                  </div>
                  <span className="text-slate-500 italic">
                    {bulkMatchBy === "gst" ? "CSV must have a GST Number column" : "CSV must have a Phone Number column (works for international / Bangladesh leads)"}
                  </span>
                </div>

                <div
                  className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 cursor-pointer"
                  onClick={() => updateFileRef.current?.click()}
                >
                  <input
                    ref={updateFileRef}
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={handleUpdateFileSelect}
                    className="hidden"
                    data-testid="bulk-update-file-input"
                  />
                  {isUploading ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
                      <p className="text-slate-600">Analyzing file...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <Upload className="w-10 h-10 text-slate-400" />
                      <p className="text-slate-600 font-medium">
                        Upload CSV with {bulkMatchBy === "gst" ? "GST Number" : "Phone Number"} + fields to update
                      </p>
                      <p className="text-sm text-slate-400">{bulkMatchBy === "gst" ? "GST Number" : "Phone Number"} column is mandatory</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {updateStep === 2 && updatePreview && (
              <div className="py-4 space-y-4">
                {/* Summary stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-blue-700">{updatePreview.leads_to_update}</p>
                    <p className="text-xs text-blue-500">Leads to update</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-emerald-700">{updatePreview.total_field_updates}</p>
                    <p className="text-xs text-emerald-500">Fields updating</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-amber-700">{updatePreview.total_field_clears}</p>
                    <p className="text-xs text-amber-500">Fields clearing</p>
                  </div>
                </div>

                {updatePreview.not_found > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                    <p className="font-semibold">{updatePreview.not_found} GST numbers not found:</p>
                    <p className="mt-1">{updatePreview.not_found_gsts?.map(g => g.gst).join(", ")}</p>
                  </div>
                )}

                {updatePreview.changes?.some(c => c.unresolved_agent) && (
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700">
                    <p className="font-semibold">Some agent emails could not be matched and will be skipped:</p>
                    <ul className="mt-1 list-disc pl-4">
                      {updatePreview.changes.filter(c => c.unresolved_agent).slice(0, 5).map((c, i) => (
                        <li key={i}><strong>{c.gst}</strong>: "{c.unresolved_agent}"</li>
                      ))}
                    </ul>
                    <p className="mt-2 text-rose-600">Tip: use the agent's exact login email (e.g., <code className="bg-rose-100 px-1 rounded">deepak@locofast.com</code>) or write <code className="bg-rose-100 px-1 rounded">BLANK</code> to unassign.</p>
                  </div>
                )}

                {updatePreview.blocked_fields?.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-700">
                    <p className="font-semibold">These columns are auto-fetched from GST and were ignored:</p>
                    <p className="mt-1">{updatePreview.blocked_fields.join(", ")}</p>
                  </div>
                )}

                <p className="text-xs text-slate-500">Mapped fields: <strong>{updatePreview.mapped_fields?.join(", ")}</strong></p>

                {/* Changes preview */}
                <div className="max-h-[250px] overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-semibold">GST / Company</th>
                        <th className="text-left p-2 font-semibold">Field</th>
                        <th className="text-left p-2 font-semibold">Old</th>
                        <th className="text-left p-2 font-semibold">New</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {updatePreview.changes?.map((ch, i) => (
                        ch.changes.map((f, j) => (
                          <tr key={`${i}-${j}`} className={f.action === 'clear' ? 'bg-amber-50' : ''}>
                            {j === 0 ? (
                              <td className="p-2 font-medium" rowSpan={ch.changes.length}>
                                <div>{ch.company_name || '-'}</div>
                                <div className="text-[10px] text-slate-400">{ch.gst}</div>
                              </td>
                            ) : null}
                            <td className="p-2 capitalize">{f.field.replace('_', ' ')}</td>
                            <td className="p-2 text-slate-500">{f.old ? f.old : <span className="italic">empty</span>}</td>
                            <td className="p-2 font-medium">
                              {f.action === 'clear' ? (
                                <span className="text-amber-600 italic">BLANK</span>
                              ) : (
                                <span className="text-emerald-700">{f.new}</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => { setUpdateStep(1); setUpdatePreview(null); setUpdateFile(null); }} className="flex-1">
                    Back
                  </Button>
                  <Button
                    onClick={handleBulkUpdateExecute}
                    disabled={isUpdating || updatePreview.leads_to_update === 0}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="execute-bulk-update-btn"
                  >
                    {isUpdating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Update {updatePreview.leads_to_update} Leads
                  </Button>
                </div>
              </div>
            )}

            {updateStep === 3 && updateResult && (
              <div className="py-6 text-center space-y-4">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Update Complete</h3>
                  <p className="text-slate-600 mt-1">{updateResult.message}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-emerald-50 rounded p-3">
                    <span className="font-bold text-emerald-700">{updateResult.updated}</span> leads updated
                  </div>
                  <div className="bg-amber-50 rounded p-3">
                    <span className="font-bold text-amber-700">{updateResult.fields_cleared}</span> fields cleared
                  </div>
                </div>
                <Button onClick={handleClose} className="w-full" data-testid="close-update-result">
                  Done
                </Button>
              </div>
            )}
          </>
        )}


        {/* ===== SAMPLE UPLOAD TAB ===== */}
        {activeTab === "samples" && (
          <div className="space-y-4">
            {sampleStep === 1 && (
              <>
                <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="font-semibold mb-1">CSV / Excel format — required columns:</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    <li><strong>GST Number</strong> <em>or</em> <strong>Phone Number</strong> (used to find the lead)</li>
                    <li><strong>Product Name</strong> (required)</li>
                    <li><strong>LFB ID</strong> (optional)</li>
                    <li><strong>Sample Date</strong> (required — YYYY-MM-DD)</li>
                  </ul>
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Match leads by:</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSampleMatchBy("gst")}
                      className={`px-3 py-1.5 rounded-md text-sm border ${sampleMatchBy === "gst" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-600"}`}
                      data-testid="sample-match-gst"
                    >GST Number</button>
                    <button
                      type="button"
                      onClick={() => setSampleMatchBy("phone")}
                      className={`px-3 py-1.5 rounded-md text-sm border ${sampleMatchBy === "phone" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-600"}`}
                      data-testid="sample-match-phone"
                    >Phone Number</button>
                  </div>
                </div>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <Upload className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                  <p className="text-sm text-slate-600 mb-3">Upload .csv / .xlsx with sample records</p>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    ref={sampleFileRef}
                    onChange={(e) => setSampleFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="sample-upload-file"
                  />
                  <label htmlFor="sample-upload-file" className="cursor-pointer">
                    <span className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">Choose File</span>
                  </label>
                  {sampleFile && <p className="mt-3 text-sm text-slate-700">{sampleFile.name}</p>}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => onClose?.()}>Cancel</Button>
                  <Button
                    disabled={!sampleFile || isSampleProcessing}
                    onClick={async () => {
                      try {
                        setIsSampleProcessing(true);
                        const fd = new FormData();
                        fd.append("file", sampleFile);
                        fd.append("match_by", sampleMatchBy);
                        const r = await axios.post(`${API}/leads/sample-upload/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                        setSamplePreview(r.data);
                        setSampleStep(2);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Preview failed");
                      } finally {
                        setIsSampleProcessing(false);
                      }
                    }}
                    data-testid="sample-preview-btn"
                  >
                    {isSampleProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Preview
                  </Button>
                </div>
              </>
            )}

            {sampleStep === 2 && samplePreview && (
              <>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-slate-50 rounded p-3"><p className="text-xs text-slate-500">Total Rows</p><p className="text-xl font-bold">{samplePreview.total_rows}</p></div>
                  <div className="bg-emerald-50 rounded p-3"><p className="text-xs text-emerald-600">Matched</p><p className="text-xl font-bold text-emerald-700" data-testid="sample-matched-count">{samplePreview.matched}</p></div>
                  <div className="bg-amber-50 rounded p-3"><p className="text-xs text-amber-600">Not Found</p><p className="text-xl font-bold text-amber-700">{samplePreview.not_found}</p></div>
                  <div className="bg-red-50 rounded p-3"><p className="text-xs text-red-600">Invalid</p><p className="text-xl font-bold text-red-700">{samplePreview.invalid}</p></div>
                </div>
                {samplePreview.preview?.length > 0 && (
                  <div className="border rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50"><tr>
                        <th className="text-left p-2">Lead</th><th className="text-left p-2">Product</th>
                        <th className="text-left p-2">LFB ID</th><th className="text-left p-2">Sample Date</th>
                      </tr></thead>
                      <tbody>
                        {samplePreview.preview.slice(0, 10).map((r, i) => (
                          <tr key={i} className="border-t"><td className="p-2">{r.company_name}</td><td className="p-2">{r.product_name}</td><td className="p-2">{r.lfb_id || "—"}</td><td className="p-2">{r.sample_date}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex justify-between gap-2">
                  <Button variant="outline" onClick={() => { setSampleStep(1); setSamplePreview(null); }}>Back</Button>
                  <Button
                    disabled={isSampleProcessing || (samplePreview.matched || 0) === 0}
                    onClick={async () => {
                      try {
                        setIsSampleProcessing(true);
                        const fd = new FormData();
                        fd.append("file", sampleFile);
                        fd.append("match_by", sampleMatchBy);
                        const r = await axios.post(`${API}/leads/sample-upload/execute`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                        setSampleResult(r.data);
                        setSampleStep(3);
                        toast.success(r.data?.message || "Done");
                        onSuccess?.();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Upload failed");
                      } finally {
                        setIsSampleProcessing(false);
                      }
                    }}
                    data-testid="sample-execute-btn"
                  >
                    {isSampleProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Attach {samplePreview.matched} Sample{samplePreview.matched === 1 ? "" : "s"}
                  </Button>
                </div>
              </>
            )}

            {sampleStep === 3 && sampleResult && (() => {
              // Build a CSV of all failed rows (unmatched + skipped) so the user
              // can fix them in Excel and re-upload.
              const failures = [
                ...(sampleResult.not_found_preview || []).map(r => ({ ...r, category: 'Unmatched' })),
                ...(sampleResult.skipped_preview || []).map(r => ({ ...r, category: 'Skipped' })),
              ];
              const hasFailures = failures.length > 0;
              const downloadFailuresCsv = () => {
                const esc = (v) => {
                  if (v === null || v === undefined) return '';
                  const s = String(v);
                  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                };
                const header = ['Row', 'Category', 'Key', 'Product Name', 'Sample Date', 'Sample Date (raw)', 'Reason'];
                const lines = [header.join(',')];
                failures.forEach(r => {
                  lines.push([
                    r.row,
                    r.category,
                    esc(r.key),
                    esc(r.product_name),
                    esc(r.sample_date || ''),
                    esc(r.sample_date_raw || ''),
                    esc(r.reason),
                  ].join(','));
                });
                // BOM so Excel picks up UTF-8 correctly
                const blob = new Blob(["\ufeff" + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `sample_upload_failures_${new Date().toISOString().slice(0,10)}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              };
              return (
              <div className="py-6 space-y-4">
                <div className="text-center space-y-2">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
                  <p className="text-lg font-semibold text-slate-900">{sampleResult.message}</p>
                </div>

                {/* Summary chips */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-emerald-700">Attached</p>
                    <p className="text-2xl font-bold text-emerald-700">{sampleResult.samples_added || 0}</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-amber-700">Unmatched</p>
                    <p className="text-2xl font-bold text-amber-700">{sampleResult.not_found || 0}</p>
                  </div>
                  <div className="bg-rose-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-rose-700">Skipped</p>
                    <p className="text-2xl font-bold text-rose-700">{sampleResult.skipped || 0}</p>
                  </div>
                </div>

                {/* Download failures button */}
                {hasFailures && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadFailuresCsv}
                      data-testid="sample-download-failures-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download failures as Excel ({failures.length})
                    </Button>
                  </div>
                )}

                {/* Per-row reasons for anything that didn't make it in */}
                {(sampleResult.not_found_preview?.length > 0) && (
                  <div className="border border-amber-200 rounded-lg overflow-hidden" data-testid="sample-not-found-list">
                    <div className="bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                      Unmatched rows ({sampleResult.not_found_preview.length}{sampleResult.not_found > sampleResult.not_found_preview.length ? ` of ${sampleResult.not_found}` : ''})
                    </div>
                    <div className="max-h-56 overflow-y-auto text-xs">
                      <table className="w-full">
                        <thead className="bg-slate-50 text-slate-500">
                          <tr>
                            <th className="text-left px-2 py-1 w-10">Row</th>
                            <th className="text-left px-2 py-1">Key</th>
                            <th className="text-left px-2 py-1">Product</th>
                            <th className="text-left px-2 py-1">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sampleResult.not_found_preview.map((r, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="px-2 py-1 text-slate-400">{r.row}</td>
                              <td className="px-2 py-1 font-mono">{r.key}</td>
                              <td className="px-2 py-1 truncate max-w-[160px]">{r.product_name}</td>
                              <td className="px-2 py-1 text-amber-700">{r.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {(sampleResult.skipped_preview?.length > 0) && (
                  <div className="border border-rose-200 rounded-lg overflow-hidden" data-testid="sample-skipped-list">
                    <div className="bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800">
                      Skipped rows ({sampleResult.skipped_preview.length}{sampleResult.skipped > sampleResult.skipped_preview.length ? ` of ${sampleResult.skipped}` : ''})
                    </div>
                    <div className="max-h-56 overflow-y-auto text-xs">
                      <table className="w-full">
                        <thead className="bg-slate-50 text-slate-500">
                          <tr>
                            <th className="text-left px-2 py-1 w-10">Row</th>
                            <th className="text-left px-2 py-1">Key</th>
                            <th className="text-left px-2 py-1">Product</th>
                            <th className="text-left px-2 py-1">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sampleResult.skipped_preview.map((r, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="px-2 py-1 text-slate-400">{r.row}</td>
                              <td className="px-2 py-1 font-mono">{r.key}</td>
                              <td className="px-2 py-1 truncate max-w-[160px]">{r.product_name}</td>
                              <td className="px-2 py-1 text-rose-700">{r.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="text-center pt-2">
                  <Button onClick={() => { setSampleStep(1); setSampleFile(null); setSamplePreview(null); setSampleResult(null); onClose?.(); }}>Close</Button>
                </div>
              </div>
              );
            })()}
          </div>
        )}


        {/* ===== ORDER UPLOAD TAB ===== */}
        {activeTab === "orders" && (
          <div>
            {orderStep === 1 && (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Match orders to existing leads by</Label>
                  <div className="flex gap-2">
                    {['gst', 'phone'].map(v => (
                      <button
                        key={v}
                        onClick={() => setOrderMatchBy(v)}
                        className={`flex-1 py-2 px-3 rounded-md text-sm font-medium border ${orderMatchBy === v ? "bg-emerald-50 border-emerald-500 text-emerald-700" : "bg-white border-slate-200 text-slate-600"}`}
                        data-testid={`order-match-${v}`}
                      >
                        {v === 'gst' ? 'GST Number' : 'Phone Number'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-700 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Required columns</p>
                      <ul className="list-disc pl-5 space-y-0.5 mt-1">
                        <li>{orderMatchBy === 'gst' ? 'GST Number' : 'Phone Number'} — match key</li>
                        <li>Order Lead ID — Locofast order reference</li>
                        <li>Fabric Type</li>
                        <li>Order Date (YYYY-MM-DD, dd/mm/yyyy, or Excel date)</li>
                        <li className="text-slate-500">Composition — optional</li>
                        <li className="text-slate-500">Quantity — optional (numeric)</li>
                        <li className="text-slate-500">Order Status — optional (Confirmed / Processing / Shipped / Out for delivery / Delivered / Cancelled)</li>
                        <li className="text-slate-500">Invoice Amount — optional (numeric)</li>
                      </ul>
                    </div>
                    <a
                      href="/samples/order_upload_template.xlsx"
                      download="order_upload_template.xlsx"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-emerald-500 bg-white text-emerald-700 hover:bg-emerald-50 whitespace-nowrap shrink-0"
                      data-testid="download-order-template-btn"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download Template
                    </a>
                  </div>
                </div>

                <div
                  className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors"
                  onClick={() => orderFileRef.current?.click()}
                  data-testid="order-upload-dropzone"
                >
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600">Click to select CSV / XLSX / XLS</p>
                  <input
                    type="file"
                    ref={orderFileRef}
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    onChange={(e) => setOrderFile(e.target.files?.[0] || null)}
                  />
                  {orderFile && <p className="mt-3 text-sm text-slate-700">{orderFile.name}</p>}
                </div>

                <div className="flex justify-end">
                  <Button
                    disabled={!orderFile || isOrderProcessing}
                    onClick={async () => {
                      if (!orderFile) return;
                      setIsOrderProcessing(true);
                      try {
                        const fd = new FormData();
                        fd.append('file', orderFile);
                        fd.append('match_by', orderMatchBy);
                        const r = await axios.post(`${API}/leads/order-upload/preview`, fd);
                        setOrderPreview(r.data);
                        setOrderStep(2);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to preview file');
                      } finally {
                        setIsOrderProcessing(false);
                      }
                    }}
                    data-testid="order-preview-btn"
                  >
                    {isOrderProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Preview
                  </Button>
                </div>
              </div>
            )}

            {orderStep === 2 && orderPreview && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-emerald-700">Matched</p>
                    <p className="text-2xl font-bold text-emerald-700">{orderPreview.matched_count}</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-amber-700">Not Found</p>
                    <p className="text-2xl font-bold text-amber-700">{orderPreview.not_found_count}</p>
                  </div>
                  <div className="bg-rose-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-rose-700">Invalid</p>
                    <p className="text-2xl font-bold text-rose-700">{orderPreview.invalid_count}</p>
                  </div>
                </div>

                {orderPreview.matched_preview?.length > 0 && (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">Preview: matched rows</div>
                    <div className="max-h-56 overflow-y-auto text-xs">
                      <table className="w-full">
                        <thead className="bg-slate-50 text-slate-500 border-t border-slate-200">
                          <tr>
                            <th className="text-left px-2 py-1 w-10">Row</th>
                            <th className="text-left px-2 py-1">Company</th>
                            <th className="text-left px-2 py-1">Order Lead ID</th>
                            <th className="text-left px-2 py-1">Fabric</th>
                            <th className="text-left px-2 py-1">Date</th>
                            <th className="text-right px-2 py-1">Qty</th>
                            <th className="text-left px-2 py-1">Status</th>
                            <th className="text-right px-2 py-1">Invoice</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orderPreview.matched_preview.map((r, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="px-2 py-1 text-slate-400">{r.row}</td>
                              <td className="px-2 py-1 truncate max-w-[140px]">{r.company_name}</td>
                              <td className="px-2 py-1 font-mono text-emerald-700">{r.order_lead_id}</td>
                              <td className="px-2 py-1">{r.fabric_type}</td>
                              <td className="px-2 py-1 text-slate-500">{r.order_date}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{r.quantity ?? '—'}</td>
                              <td className="px-2 py-1 text-slate-600">{r.order_status || '—'}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{r.invoice_amount != null ? r.invoice_amount.toLocaleString() : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => { setOrderStep(1); setOrderFile(null); setOrderPreview(null); }}>Back</Button>
                  <Button
                    disabled={orderPreview.matched_count === 0 || isOrderProcessing}
                    onClick={async () => {
                      setIsOrderProcessing(true);
                      try {
                        const fd = new FormData();
                        fd.append('file', orderFile);
                        fd.append('match_by', orderMatchBy);
                        const r = await axios.post(`${API}/leads/order-upload/execute`, fd);
                        setOrderResult(r.data);
                        setOrderStep(3);
                        onImportComplete?.();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to execute upload');
                      } finally {
                        setIsOrderProcessing(false);
                      }
                    }}
                    data-testid="order-execute-btn"
                  >
                    {isOrderProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Attach {orderPreview.matched_count} order{orderPreview.matched_count === 1 ? '' : 's'}
                  </Button>
                </div>
              </div>
            )}

            {orderStep === 3 && orderResult && (() => {
              const failures = [
                ...(orderResult.not_found_preview || []).map(r => ({ ...r, category: 'Unmatched' })),
                ...(orderResult.skipped_preview || []).map(r => ({ ...r, category: 'Skipped' })),
              ];
              const hasFailures = failures.length > 0;
              const downloadFailuresCsv = () => {
                const esc = (v) => {
                  if (v === null || v === undefined) return '';
                  const s = String(v);
                  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                };
                const header = ['Row', 'Category', 'Key', 'Order Lead ID', 'Fabric Type', 'Order Date', 'Order Date (raw)', 'Reason'];
                const lines = [header.join(',')];
                failures.forEach(r => {
                  lines.push([r.row, r.category, esc(r.key), esc(r.order_lead_id), esc(r.fabric_type),
                              esc(r.order_date || ''), esc(r.sample_date_raw || ''), esc(r.reason)].join(','));
                });
                const blob = new Blob(["\ufeff" + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `order_upload_failures_${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              };
              return (
                <div className="py-6 space-y-4">
                  <div className="text-center space-y-2">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
                    <p className="text-lg font-semibold text-slate-900">{orderResult.message}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-emerald-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-emerald-700">Attached</p>
                      <p className="text-2xl font-bold text-emerald-700">{orderResult.orders_added || 0}</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-amber-700">Unmatched</p>
                      <p className="text-2xl font-bold text-amber-700">{orderResult.not_found || 0}</p>
                    </div>
                    <div className="bg-rose-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-rose-700">Skipped</p>
                      <p className="text-2xl font-bold text-rose-700">{orderResult.skipped || 0}</p>
                    </div>
                  </div>
                  {hasFailures && (
                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" onClick={downloadFailuresCsv} data-testid="order-download-failures-btn">
                        <Download className="w-4 h-4 mr-2" />Download failures as Excel ({failures.length})
                      </Button>
                    </div>
                  )}
                  {orderResult.not_found_preview?.length > 0 && (
                    <div className="border border-amber-200 rounded-lg overflow-hidden">
                      <div className="bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                        Unmatched rows ({orderResult.not_found_preview.length}{orderResult.not_found > orderResult.not_found_preview.length ? ` of ${orderResult.not_found}` : ''})
                      </div>
                      <div className="max-h-56 overflow-y-auto text-xs">
                        <table className="w-full">
                          <thead className="bg-slate-50 text-slate-500">
                            <tr>
                              <th className="text-left px-2 py-1 w-10">Row</th>
                              <th className="text-left px-2 py-1">Key</th>
                              <th className="text-left px-2 py-1">Order Lead ID</th>
                              <th className="text-left px-2 py-1">Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderResult.not_found_preview.map((r, i) => (
                              <tr key={i} className="border-t border-slate-100">
                                <td className="px-2 py-1 text-slate-400">{r.row}</td>
                                <td className="px-2 py-1 font-mono">{r.key}</td>
                                <td className="px-2 py-1 truncate max-w-[140px]">{r.order_lead_id}</td>
                                <td className="px-2 py-1 text-amber-700">{r.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {orderResult.skipped_preview?.length > 0 && (
                    <div className="border border-rose-200 rounded-lg overflow-hidden">
                      <div className="bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800">
                        Skipped rows ({orderResult.skipped_preview.length}{orderResult.skipped > orderResult.skipped_preview.length ? ` of ${orderResult.skipped}` : ''})
                      </div>
                      <div className="max-h-56 overflow-y-auto text-xs">
                        <table className="w-full">
                          <thead className="bg-slate-50 text-slate-500">
                            <tr>
                              <th className="text-left px-2 py-1 w-10">Row</th>
                              <th className="text-left px-2 py-1">Key</th>
                              <th className="text-left px-2 py-1">Order Lead ID</th>
                              <th className="text-left px-2 py-1">Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderResult.skipped_preview.map((r, i) => (
                              <tr key={i} className="border-t border-slate-100">
                                <td className="px-2 py-1 text-slate-400">{r.row}</td>
                                <td className="px-2 py-1 font-mono">{r.key}</td>
                                <td className="px-2 py-1 truncate max-w-[140px]">{r.order_lead_id}</td>
                                <td className="px-2 py-1 text-rose-700">{r.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  <div className="text-center pt-2">
                    <Button onClick={() => { setOrderStep(1); setOrderFile(null); setOrderPreview(null); setOrderResult(null); onClose?.(); }}>Close</Button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}



        {/* ===== WEEKLY UPDATE TAB (Super Admin only) ===== */}
        {activeTab === "weekly" && isSuperAdmin && (
          <>
            {/* Last upload banner — visible across all steps */}
            {weeklyHistory.length > 0 && (
              <div
                className="mt-3 flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600"
                data-testid="weekly-last-upload-banner"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>
                    Last upload <strong className="text-slate-800">{formatRelative(weeklyHistory[0].uploaded_at)}</strong>
                    {" "}({new Date(weeklyHistory[0].uploaded_at).toLocaleString()})
                  </span>
                  <span className="text-slate-400">by {weeklyHistory[0].uploaded_by}</span>
                  <span className="text-slate-400">
                    • {weeklyHistory[0].matched} flagged, {weeklyHistory[0].total_orders_added} orders added
                    {weeklyHistory[0].unmatched ? `, ${weeklyHistory[0].unmatched} unmatched` : ''}
                  </span>
                </div>
              </div>
            )}
            {weeklyHistory.length === 0 && (
              <div
                className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700"
                data-testid="weekly-no-upload-banner"
              >
                No weekly upload has been run yet.
              </div>
            )}

            {weeklyStep === 1 && (
              <div className="py-4 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 space-y-1">
                  <p className="font-semibold">How it works (every Wednesday 12:00 Noon):</p>
                  <p>- Required column: <strong>GST Number</strong></p>
                  <p>- Optional column: <strong>Order Count</strong> (last 7 days). Empty / 0 / column missing = query-only</p>
                  <p>- Every matched lead → <strong>Query Exists</strong> flag set (sticky — survives Pick Pool / LFB)</p>
                  <p>- If Order Count &gt; 0 → also <strong>added cumulatively</strong> to lead's `order_count`</p>
                  <p>- Source field is <strong>NEVER modified</strong>. ICP scoring priority: Order Exists &gt; Query Exists &gt; original Source</p>
                  <p>- Unmatched GSTs are reported but skipped (no auto-create)</p>
                </div>
                <div
                  className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 cursor-pointer"
                  onClick={() => weeklyFileRef.current?.click()}
                  data-testid="weekly-upload-dropzone"
                >
                  <input
                    ref={weeklyFileRef}
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={handleWeeklyFileSelect}
                    className="hidden"
                    data-testid="weekly-upload-file-input"
                  />
                  {isUploadingWeekly ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
                      <p className="text-slate-600">Analyzing file...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <Upload className="w-10 h-10 text-slate-400" />
                      <p className="text-slate-600 font-medium">Upload weekly CSV/Excel</p>
                      <p className="text-sm text-slate-400">Columns: GST Number (required), Order Count (optional)</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {weeklyStep === 2 && weeklyPreview && (
              <div className="py-4 space-y-4">
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-emerald-700" data-testid="weekly-matched-count">{weeklyPreview.matched_count}</p>
                    <p className="text-[11px] text-emerald-500">Matched</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-purple-700" data-testid="weekly-new-query-flags">{weeklyPreview.new_query_flags}</p>
                    <p className="text-[11px] text-purple-500">New Query Flags</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-blue-700" data-testid="weekly-orders-to-add">{weeklyPreview.total_orders_to_add}</p>
                    <p className="text-[11px] text-blue-500">Orders Adding</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-amber-700" data-testid="weekly-unmatched-count">{weeklyPreview.unmatched_count}</p>
                    <p className="text-[11px] text-amber-500">Unmatched</p>
                  </div>
                </div>

                <p className="text-xs text-slate-500">
                  GST column: <strong>{weeklyPreview.gst_column}</strong>
                  {weeklyPreview.has_count_column ? (
                    <> • Count column: <strong>{weeklyPreview.count_column}</strong></>
                  ) : (
                    <> • <span className="text-amber-600">No Order Count column detected — query-only mode</span></>
                  )}
                </p>

                {weeklyPreview.parse_errors?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                    <p className="font-semibold">{weeklyPreview.parse_errors.length} parse warning(s):</p>
                    <p className="mt-1">{weeklyPreview.parse_errors.slice(0, 5).join(" • ")}{weeklyPreview.parse_errors.length > 5 ? "…" : ""}</p>
                  </div>
                )}

                {weeklyPreview.matched?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-700 mb-1">Matched leads (first {weeklyPreview.matched.length})</p>
                    <div className="max-h-[260px] overflow-y-auto border rounded-lg">
                      <table className="w-full text-xs" data-testid="weekly-matched-table">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-semibold">Company / GST</th>
                            <th className="text-left p-2 font-semibold">Source</th>
                            <th className="text-center p-2 font-semibold">Query?</th>
                            <th className="text-right p-2 font-semibold">Existing</th>
                            <th className="text-right p-2 font-semibold">Adding</th>
                            <th className="text-right p-2 font-semibold">New Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {weeklyPreview.matched.map((row, i) => (
                            <tr key={i} className={row.adding > 0 ? "bg-blue-50/30" : ""}>
                              <td className="p-2">
                                <div className="font-medium">{row.company_name || "-"}</div>
                                <div className="text-[10px] text-slate-400">{row.gst_number}</div>
                              </td>
                              <td className="p-2 text-slate-600">{row.data_source || "-"}</td>
                              <td className="p-2 text-center">
                                {row.was_query_flagged ? (
                                  <span className="text-slate-400 text-[10px]">existing</span>
                                ) : (
                                  <span className="text-purple-600 font-medium text-[10px]">+ NEW</span>
                                )}
                              </td>
                              <td className="p-2 text-right text-slate-500">{row.existing_count}</td>
                              <td className="p-2 text-right">
                                {row.adding > 0 ? (
                                  <span className="text-blue-600 font-medium">+{row.adding}</span>
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </td>
                              <td className="p-2 text-right font-bold text-emerald-700">{row.new_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {weeklyPreview.unmatched?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-700 mb-1">Unmatched GSTs (not in CRM, will be skipped)</p>
                    <div className="max-h-[120px] overflow-y-auto bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
                      {weeklyPreview.unmatched.map((u, i) => (
                        <span key={i} className="inline-block mr-2 mb-1">
                          {u.gst_number}{u.adding > 0 ? <span className="text-amber-500"> (+{u.adding})</span> : null}{i < weeklyPreview.unmatched.length - 1 ? "," : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => { setWeeklyStep(1); setWeeklyPreview(null); setWeeklyFile(null); }}
                    className="flex-1"
                    data-testid="weekly-back-btn"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleWeeklyExecute}
                    disabled={isExecutingWeekly || weeklyPreview.matched_count === 0}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="execute-weekly-upload-btn"
                  >
                    {isExecutingWeekly ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Apply to {weeklyPreview.matched_count} Leads
                  </Button>
                </div>
              </div>
            )}

            {weeklyStep === 3 && weeklyResult && (
              <div className="py-6 text-center space-y-4" data-testid="weekly-result">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Weekly Update Applied</h3>
                  <p className="text-slate-600 mt-1">{weeklyResult.message}</p>
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="bg-purple-50 rounded p-3">
                    <span className="font-bold text-purple-700">{weeklyResult.matched}</span>
                    <p className="text-xs text-purple-600">Leads flagged Query</p>
                  </div>
                  <div className="bg-blue-50 rounded p-3">
                    <span className="font-bold text-blue-700">{weeklyResult.total_orders_added}</span>
                    <p className="text-xs text-blue-600">Orders added</p>
                  </div>
                  <div className="bg-amber-50 rounded p-3">
                    <span className="font-bold text-amber-700">{weeklyResult.unmatched}</span>
                    <p className="text-xs text-amber-600">Unmatched</p>
                  </div>
                </div>
                <Button onClick={handleClose} className="w-full" data-testid="close-weekly-result">
                  Done
                </Button>
              </div>
            )}
          </>
        )}


        {/* ===== IMPORT TAB ===== */}
        {activeTab === "import" && (
          <>
        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="py-6">
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="csv-file-input"
              />
              {isUploading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
                  <p className="text-slate-600">Processing file...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <Upload className="w-10 h-10 text-slate-400" />
                  <div>
                    <p className="text-slate-600 font-medium">Click to upload file</p>
                    <p className="text-sm text-slate-400 mt-1">CSV, XLS, or XLSX</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Sample Files Download Section */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Download className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-blue-800 text-sm">Download Unified Import Template</p>
                  <p className="text-xs text-blue-700 mb-3">
                    One format for all data sources (India + Bangladesh). Mandatory fields marked with *. Dropdowns for Data Source, GST Turnover, Has Website, Business Type, Fabric Category.
                  </p>
                  <div className="flex gap-2">
                    <a
                      href="/samples/locofast_import_template.xlsx"
                      download="locofast_import_template.xlsx"
                      className="flex items-center gap-2 text-sm bg-white border border-blue-300 rounded px-4 py-2 text-blue-700 hover:bg-blue-100 transition-colors font-medium"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="download-unified-template"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      Download Full Template
                    </a>
                    <a
                      href="/samples/locofast_update_template.xlsx"
                      download="locofast_update_template.xlsx"
                      className="flex items-center gap-2 text-sm bg-white border border-amber-300 rounded px-4 py-2 text-amber-700 hover:bg-amber-100 transition-colors font-medium"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="download-update-template"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      Download Update Template
                    </a>
                  </div>
                  <p className="text-[11px] text-amber-600 mt-1">Update Template: Fill Phone + Business Type + GST Turnover to patch existing leads (no need to re-upload everything)</p>
                  <div className="mt-3 text-xs text-blue-600 space-y-1">
                    <p><strong>Mandatory (India — all sources):</strong> Company Name, Contact Person, Phone, Email, Data Source, GST Number, GST Reg. Date (2yr+), GST Turnover (40L+), Has Website, Business Type, City, State</p>
                    <p><strong>Bangladesh Customers:</strong> No GST fields required. BIN Number optional (required for Pick Pool). Manual call logging.</p>
                    <p><strong>"Query exists" / "Meta":</strong> Fabric Category also required</p>
                    <p><strong>Optional:</strong> Website, Fabric Type, Composition, Previous Status, Notes</p>
                    <p><strong>Orders:</strong> Order 1-10 (Lead ID, Fabric Type, Composition, Date)</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 text-sm text-slate-500">
              <p className="font-medium mb-2">Validation rules:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>GST Registration Date</strong> — required (India, non-"Order exists")</li>
                <li><strong>GST Turnover</strong> — all brackets accepted (0-40L, 40L-1.5Cr, …, 500Cr+)</li>
                <li><strong>Email</strong> — mandatory for all data sources</li>
                <li><strong>Has Website / Business Type</strong> — mandatory for all</li>
                <li><strong>Fabric Category</strong> — mandatory for "Query exists" and "Meta"</li>
                <li><strong>Bangladesh Customers</strong> — GST fields skipped, BIN Number optional</li>
              </ul>
              <p className="text-xs text-amber-600 mt-2">
                <strong>Note:</strong> "Order exists" skips GST date/turnover checks. "Bangladesh Customers" skips all GST checks.
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Preview and Map */}
        {step === 2 && previewData && (
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">
                <strong>{previewData.total_rows}</strong> rows found in file
              </span>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                <X className="w-4 h-4 mr-1" />
                Change file
              </Button>
            </div>

            {/* Column Mapping */}
            <div className="space-y-3">
              <h4 className="font-medium text-slate-900">Map File Columns</h4>
              <div className="space-y-1.5 max-w-md">
                {previewData.headers
                  .filter(header => header && String(header).trim() !== '')
                  .map((header) => {
                    const headerStr = String(header);
                    return (
                      <div key={headerStr} className="flex items-center gap-2">
                        <span className="text-sm text-slate-700 w-40 truncate flex-shrink-0" title={headerStr}>{headerStr}</span>
                        <span className="text-slate-300 text-xs">→</span>
                        <Select
                          value={columnMapping[headerStr] || "_skip_"}
                          onValueChange={(value) => updateMapping(headerStr, value === "_skip_" ? "" : value)}
                        >
                          <SelectTrigger className="w-48 h-8 text-xs" data-testid={`map-${headerStr}`}>
                            <SelectValue placeholder="Map to..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availableFields.map((field) => (
                              <SelectItem key={field.value} value={field.value}>
                                {field.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Preview Table */}
            <div className="space-y-2">
              <h4 className="font-medium text-slate-900">Preview (first 5 rows)</h4>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      {previewData.headers.map((header) => (
                        <th key={header} className="text-left px-3 py-2 text-xs font-medium text-slate-600">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {previewData.preview_rows.map((row, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        {previewData.headers.map((header) => (
                          <td key={header} className="px-3 py-2 text-slate-700 truncate max-w-[150px]">
                            {row[header] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={handleImport}
                disabled={isImporting || !Object.values(columnMapping).includes('company_name')}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="import-csv-btn"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import {previewData.total_rows} Leads
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 3: Results */}
        {step === 3 && importResult && (
          <div className="py-6 space-y-6">
            <div className="text-center">
              {(importResult.imported > 0 || importResult.updated > 0) ? (
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              ) : (
                <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              )}
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                {isUpdateMode ? "Update Complete" : "Import Complete"}
              </h3>
            </div>

            {isUpdateMode ? (
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-emerald-600">{importResult.updated || 0}</p>
                  <p className="text-sm text-slate-600">Updated</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-amber-600">{importResult.not_found || 0}</p>
                  <p className="text-sm text-slate-600">Not Found</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-red-600">{importResult.errors || 0}</p>
                  <p className="text-sm text-slate-600">Errors</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-emerald-600">{importResult.imported}</p>
                  <p className="text-sm text-slate-600">Imported</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-blue-600">{importResult.gst_enriched || 0}</p>
                  <p className="text-sm text-slate-600">GST Auto-filled</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-amber-600">{importResult.duplicates}</p>
                  <p className="text-sm text-slate-600">Duplicates</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-red-600">{importResult.errors}</p>
                  <p className="text-sm text-slate-600">Errors</p>
                </div>
              </div>
            )}

            {importResult.error_details?.length > 0 && (
              <div className="bg-red-50 rounded-lg p-4">
                <h4 className="font-medium text-red-800 mb-2">Error Details:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {importResult.error_details.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <DialogFooter>
              <Button onClick={handleClose} className="w-full">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CSVImport;
